import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import {
  getAttendanceSettings,
  updateAttendanceSettings,
  listShifts,
  createShift,
  updateShift,
  deleteShift,
  syncShiftUsers,
  listHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  listAttendanceStaff,
} from "../../api/attendanceService";
import { nowIst, formatDate } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

const WEEKDAYS = [
  { iso: 1, label: "Mon" },
  { iso: 2, label: "Tue" },
  { iso: 3, label: "Wed" },
  { iso: 4, label: "Thu" },
  { iso: 5, label: "Fri" },
  { iso: 7, label: "Sun" },
];

const SATURDAY_MODES = [
  { value: "all", label: "All Saturdays working" },
  { value: "none", label: "All Saturdays off" },
  { value: "first_third", label: "1st & 3rd Saturdays working" },
  { value: "second_fourth", label: "2nd & 4th Saturdays working" },
  { value: "even_weeks", label: "Even ISO weeks working" },
  { value: "odd_weeks", label: "Odd ISO weeks working" },
];

export default function AttendanceSettingsPage() {
  const [tab, setTab] = useState("rules");

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Attendance Settings</h1>
          <div className="page-sub">Working week, thresholds, shifts and the holiday calendar.</div>
        </div>
        <div className="page-actions">
          <Link className="btn btn-secondary" to="/admin/attendance">
            <Icon name="chevron-left" size={14} /> Register
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="tabs" style={{ marginBottom: 14 }}>
            <button className={`tab ${tab === "rules" ? "active" : ""}`} onClick={() => setTab("rules")}>Rules</button>
            <button className={`tab ${tab === "shifts" ? "active" : ""}`} onClick={() => setTab("shifts")}>Shifts</button>
            <button className={`tab ${tab === "holidays" ? "active" : ""}`} onClick={() => setTab("holidays")}>Holidays</button>
          </div>

          {tab === "rules" && <RulesTab />}
          {tab === "shifts" && <ShiftsTab />}
          {tab === "holidays" && <HolidaysTab />}
        </div>
      </div>
    </>
  );
}

/* ── Rules ───────────────────────────────────────────────────────────── */

function RulesTab() {
  const settings = useApi(() => getAttendanceSettings());
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const d = settings.data?.data;
    if (d && !form) {
      setForm({
        working_days: d.working_days || [1, 2, 3, 4, 5],
        saturday_mode: d.saturday_mode || "all",
        grace_minutes: d.grace_minutes ?? 15,
        full_day_minutes: d.full_day_minutes ?? 480,
        half_day_minutes: d.half_day_minutes ?? 240,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  if (settings.loading || !form) return <LoadingBlock rows={4} />;

  const toggleDay = (iso) =>
    setForm((f) => ({
      ...f,
      working_days: f.working_days.includes(iso)
        ? f.working_days.filter((d) => d !== iso)
        : [...f.working_days, iso].sort(),
    }));

  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      await updateAttendanceSettings(form);
      showToast("success", "Attendance rules saved");
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="col gap-md" style={{ maxWidth: 560 }}>
      <div className="field">
        <label className="field-label">Working days (Saturday is configured separately)</label>
        <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
          {WEEKDAYS.map((d) => (
            <label key={d.iso} className={`chip ${form.working_days.includes(d.iso) ? "active" : ""}`} style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.working_days.includes(d.iso)}
                onChange={() => toggleDay(d.iso)}
                style={{ marginRight: 6 }}
              />
              {d.label}
            </label>
          ))}
        </div>
        {errors.working_days && <div className="field-error">{String(errors.working_days)}</div>}
      </div>

      <div className="field">
        <label className="field-label">Saturdays</label>
        <select className="select" value={form.saturday_mode} onChange={(e) => setForm((f) => ({ ...f, saturday_mode: e.target.value }))}>
          {SATURDAY_MODES.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <div className="field-hint">Covers Mon–Fri, Mon–Sat, Sun–Fri and alternate-Saturday firms. A 5th Saturday follows the 1st/3rd pattern.</div>
      </div>

      <div className="row gap-md" style={{ flexWrap: "wrap" }}>
        <NumField label="Late grace (min)" value={form.grace_minutes} onChange={(v) => setForm((f) => ({ ...f, grace_minutes: v }))} error={errors.grace_minutes} hint="After shift start" />
        <NumField label="Full day (min)" value={form.full_day_minutes} onChange={(v) => setForm((f) => ({ ...f, full_day_minutes: v }))} error={errors.full_day_minutes} hint="480 = 8h" />
        <NumField label="Half day (min)" value={form.half_day_minutes} onChange={(v) => setForm((f) => ({ ...f, half_day_minutes: v }))} error={errors.half_day_minutes} hint="240 = 4h" />
      </div>

      <div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save rules"}
        </button>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, error, hint }) {
  return (
    <div className="field" style={{ width: 150 }}>
      <label className="field-label">{label}</label>
      <input type="number" className="input" value={value} onChange={(e) => onChange(Number(e.target.value))} />
      {hint && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{String(error)}</div>}
    </div>
  );
}

/* ── Shifts ──────────────────────────────────────────────────────────── */

function ShiftsTab() {
  const shifts = useApi(() => listShifts());
  const staff = useApi(() => listAttendanceStaff());
  const [drawer, setDrawer] = useState(null); // null | {shift?} for create/edit

  const rows = shifts.data?.data || [];
  const staffRows = staff.data?.data || [];

  const onDelete = async (s) => {
    if (!window.confirm(`Delete shift "${s.name}"? Assignments are removed; history keeps its snapshot.`)) return;
    try {
      await deleteShift(s.id);
      showToast("success", "Shift deleted");
      shifts.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "flex-end", marginBottom: 10 }}>
        <button className="btn btn-primary" onClick={() => setDrawer({})}>
          <Icon name="plus" size={14} /> New shift
        </button>
      </div>
      {shifts.loading ? (
        <LoadingBlock rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState icon="clock" title="No shifts yet" hint="Create a shift (e.g. 10:00–19:00) and assign your team to it — late tracking needs one." />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Timing</th>
              <th>Assigned</th>
              <th>Status</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td className="t-primary fw-500">{s.name}</td>
                <td>{String(s.start_time).slice(0, 5)} – {String(s.end_time).slice(0, 5)}</td>
                <td>
                  {(s.users || []).length === 0 ? (
                    <span className="t-muted text-xs">Nobody</span>
                  ) : (
                    <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
                      {s.users.map((u) => (
                        <span key={u.id} className="badge badge-neutral">{u.name}{u.role === "admin" ? " (admin)" : ""}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td>{s.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-neutral">Off</span>}</td>
                <td>
                  <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                    <button className="icon-btn" title="Edit" onClick={() => setDrawer({ shift: s })}>
                      <Icon name="edit" size={13} />
                    </button>
                    <button className="icon-btn" title="Delete" onClick={() => onDelete(s)} style={{ color: "var(--danger)" }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ShiftDrawer
        open={!!drawer}
        shift={drawer?.shift}
        staff={staffRows}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          shifts.reload();
        }}
      />
    </>
  );
}

function ShiftDrawer({ open, shift, staff, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("19:00");
  const [isActive, setIsActive] = useState(true);
  const [assigned, setAssigned] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (shift) {
      setName(shift.name || "");
      setStart(String(shift.start_time).slice(0, 5));
      setEnd(String(shift.end_time).slice(0, 5));
      setIsActive(shift.is_active !== false);
      setAssigned((shift.users || []).map((u) => u.id));
    } else {
      setName("");
      setStart("10:00");
      setEnd("19:00");
      setIsActive(true);
      setAssigned([]);
    }
    setErrors({});
  }, [open, shift]);

  const toggle = (id) => setAssigned((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const submit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const payload = { name: name.trim(), start_time: start, end_time: end, is_active: isActive };
      let id = shift?.id;
      if (shift) {
        await updateShift(id, payload);
      } else {
        const res = await createShift(payload);
        id = res?.data?.id;
      }
      if (id != null) await syncShiftUsers(id, assigned);
      showToast("success", shift ? "Shift updated" : "Shift created");
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={shift ? `Edit ${shift.name}` : "New shift"}
      subtitle="One shift per person — assigning someone here moves them off any other shift."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : shift ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. General" />
          {errors.name && <div className="field-error">{String(errors.name)}</div>}
        </div>
        <div className="row gap-md">
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Starts</label>
            <input type="time" className="input" value={start} onChange={(e) => setStart(e.target.value)} />
            {errors.start_time && <div className="field-error">{String(errors.start_time)}</div>}
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">Ends</label>
            <input type="time" className="input" value={end} onChange={(e) => setEnd(e.target.value)} />
            {errors.end_time && <div className="field-error">{String(errors.end_time)}</div>}
          </div>
        </div>
        <div className="row gap-sm">
          <input id="shift-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <label htmlFor="shift-active" className="text-sm" style={{ cursor: "pointer", marginBottom: 0 }}>Active</label>
        </div>
        <div className="field">
          <label className="field-label">Assign staff (admins included)</label>
          <div className="col gap-sm">
            {staff.length === 0 ? (
              <div className="t-muted text-xs">No staff found.</div>
            ) : (
              staff.map((u) => {
                const id = `shift-user-${u.id}`;
                return (
                  <label key={u.id} htmlFor={id} className="row gap-sm" style={{ cursor: "pointer" }}>
                    <input id={id} type="checkbox" checked={assigned.includes(u.id)} onChange={() => toggle(u.id)} />
                    <span className="text-sm">
                      {u.name} <span className="t-muted text-xs">{u.role === "admin" ? "Admin" : u.department || "Employee"}</span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
}

/* ── Holidays ────────────────────────────────────────────────────────── */

function HolidaysTab() {
  const [year, setYear] = useState(nowIst().year());
  const holidays = useApi(() => listHolidays(year), [year]);
  const [drawer, setDrawer] = useState(null);

  const rows = holidays.data?.data || [];

  const onDelete = async (h) => {
    if (!window.confirm(`Remove holiday "${h.name}"?`)) return;
    try {
      await deleteHoliday(h.id);
      showToast("success", "Holiday removed");
      holidays.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <select className="select" style={{ width: 120 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button className="btn btn-primary" onClick={() => setDrawer({})}>
          <Icon name="plus" size={14} /> Add holiday
        </button>
      </div>
      {holidays.loading ? (
        <LoadingBlock rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState icon="calendar" title={`No holidays in ${year}`} hint="Add your firm's holiday list — holidays never count against attendance or leave." />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id}>
                <td>{formatDate(h.date)}</td>
                <td className="t-primary">{h.name}</td>
                <td>
                  <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                    <button className="icon-btn" title="Edit" onClick={() => setDrawer({ holiday: h })}>
                      <Icon name="edit" size={13} />
                    </button>
                    <button className="icon-btn" title="Delete" onClick={() => onDelete(h)} style={{ color: "var(--danger)" }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <HolidayDrawer
        open={!!drawer}
        holiday={drawer?.holiday}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          holidays.reload();
        }}
      />
    </>
  );
}

function HolidayDrawer({ open, holiday, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(holiday ? String(holiday.date).slice(0, 10) : "");
    setName(holiday?.name || "");
    setErrors({});
  }, [open, holiday]);

  const submit = async () => {
    setSaving(true);
    setErrors({});
    try {
      if (holiday) {
        await updateHoliday(holiday.id, { date, name: name.trim() });
      } else {
        await createHoliday({ date, name: name.trim() });
      }
      showToast("success", holiday ? "Holiday updated" : "Holiday added");
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={holiday ? "Edit holiday" : "Add holiday"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Date</label>
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
          {errors.date && <div className="field-error">{String(errors.date)}</div>}
        </div>
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Diwali" />
          {errors.name && <div className="field-error">{String(errors.name)}</div>}
        </div>
      </div>
    </Drawer>
  );
}
